import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API methods
export const apiService = {
  // Module endpoints
  async getModules() {
    try {
      const response = await apiClient.get('/modules');
      return response.data;
    } catch (error) {
      console.error('Error fetching modules:', error);
      throw new Error('Failed to load modules. Please try again.');
    }
  },

  async getModule(moduleId) {
    try {
      const response = await apiClient.get(`/modules/${moduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching module ${moduleId}:`, error);
      if (error.response?.status === 404) {
        throw new Error('Module not found');
      }
      throw new Error('Failed to load module. Please try again.');
    }
  },

  async getModuleQuiz(moduleId) {
    try {
      const response = await apiClient.get(`/modules/${moduleId}/quiz`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching quiz for module ${moduleId}:`, error);
      if (error.response?.status === 404) {
        throw new Error('Quiz not found');
      }
      throw new Error('Failed to load quiz. Please try again.');
    }
  },

  // Quiz result endpoints
  async saveQuizResult(quizResult) {
    try {
      const response = await apiClient.post('/quiz-results', quizResult);
      return response.data;
    } catch (error) {
      console.error('Error saving quiz result:', error);
      throw new Error('Failed to save quiz result. Please try again.');
    }
  },

  async getQuizStats() {
    try {
      const response = await apiClient.get('/quiz-results/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching quiz stats:', error);
      throw new Error('Failed to load quiz statistics. Please try again.');
    }
  },

  // Reading progress endpoints
  async updateReadingProgress(sessionId, moduleId, sectionIndex, isRead) {
    try {
      const response = await apiClient.post('/reading-progress', {
        sessionId,
        moduleId,
        sectionIndex,
        isRead
      });
      return response.data;
    } catch (error) {
      console.error('Error updating reading progress:', error);
      throw new Error('Failed to update reading progress. Please try again.');
    }
  },

  async getReadingProgress(sessionId) {
    try {
      const response = await apiClient.get(`/reading-progress/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reading progress:', error);
      throw new Error('Failed to load reading progress. Please try again.');
    }
  },

  async getModuleProgressSummary(sessionId) {
    try {
      const response = await apiClient.get(`/module-progress/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching module progress:', error);
      throw new Error('Failed to load module progress. Please try again.');
    }
  },

  // Module completion endpoints
  async updateModuleCompletion(sessionId, moduleId, quizCompleted, allSectionsRead) {
    try {
      const response = await apiClient.post('/module-completion', {
        sessionId,
        moduleId,
        quizCompleted,
        allSectionsRead
      });
      return response.data;
    } catch (error) {
      console.error('Error updating module completion:', error);
      throw new Error('Failed to update module completion. Please try again.');
    }
  },

  async getModuleCompletions(sessionId) {
    try {
      const response = await apiClient.get(`/module-completion/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching module completions:', error);
      throw new Error('Failed to load module completions. Please try again.');
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await apiClient.get('/');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw new Error('Backend service unavailable');
    }
  }
};

export default apiService;